import { DAY_MS } from "./freshness.js";
import { epley, isE1rmRepCount, roundToHalfKg } from "./pr.js";
import { effectiveRir } from "./effortScale.js";
import type { Workout, WorkoutSet } from "./schemas/workout.js";
import type { MaxSource } from "./schemas/max.js";

// Bidirectional MAX — the LOWER half. The RAISE half lives in maxSuggest.ts
// (suggestMaxUpdate / suggestMaxUpdatesForWorkout); this module is its
// mirror. It proposes pulling a stored MAX *down* when a genuine
// maximal-effort set proves the ceiling has dropped (detraining, injury,
// age). Spec: next/coaching.md §9, Antenore 2026-06-17. Today the stored
// MAX is a one-way ratchet — it rises on a new best but nothing ever
// pulls it down, so after a layoff it sits stale-high and every percent
// prescription comes out too heavy. This closes the loop.
//
// The asymmetry is the whole point:
//   - RAISE: ANY set whose e1RM exceeds the MAX is valid evidence — if
//     you lifted w×r your 1RM is at least the e1RM, regardless of effort.
//   - LOWER: ONLY a true ceiling attempt (RIR 0 or fail) counts. A
//     submaximal set that computes low proves nothing — you just didn't
//     push. ⇒ a deliberately-light block (RIR 2-3, e.g. the hernia
//     "Light" program) almost never qualifies, so the system self-parks
//     in light phases and re-evaluates only when the athlete genuinely
//     grinds a set to failure on return to heavy work. Exactly desired:
//     it never punishes you for training light on purpose.
//
// Never auto-writes — the athlete is the final judge (the end-of-session
// confirm prompt, see useMaxBumpCandidates). On confirm the lowered value
// is stored with source:"tested" (it came from a real failed/ceiling
// attempt), which also protects it from a later seed-from-history
// re-derive bumping it back up (suggestMaxesFromHistory preserves
// manual/tested, overwrites only auto-origin seeds).

// Day-to-day within-subject 1RM swing is a few % (literature ~3-5%), so a
// 5% band below the stored MAX avoids false alarms from normal noise.
export const MAX_REGRESSION_DELTA = 0.05;

// A full training cycle must pass before a downward revision is plausible
// (most blocks are 6 weeks, Antenore 2026-06-17) — anti-thrash.
export const MAX_REGRESSION_STALE_WEEKS = 6;

// Recent-ceiling corroboration window (Antenore 2026-06-26). One saved
// session is a single data point: a light/off-day RIR0 back-off set can
// compute below the noise band and falsely pull the MAX down even though
// a session a few days earlier proved a higher ceiling. We therefore look
// back this far for the best qualifying e1RM and refuse to lower when a
// recent session was at least as strong — a real decline is sustained,
// not one light day. 4 weeks ≈ a mesocycle of corroboration.
export const MAX_REGRESSION_RECENT_WEEKS = 4;

const WEEK_MS = 7 * DAY_MS;

// The stored MAX a regression is measured against: value + when it was
// set. `setAt` drives the staleness gate (the MAX schema's own field —
// no new persisted field needed).
export type CurrentMax = {
  valueKg: number;
  setAt: string;
  /** Where the value came from. Optional because the regression check does
   *  not need it — it is carried for the readers that must decide whether a
   *  MAX may be used to PROPOSE a load (isMaxTrustedForProposal, EQ-1-SWAP-
   *  PCTMAX). Absent means "unknown", which those readers treat as untrusted,
   *  never as trusted. */
  source?: MaxSource;
};

export type MaxRegression = {
  // Proposed lower value (rounded to 0.5 kg) = the strongest qualifying
  // ceiling attempt in the session. Conservative on the way DOWN: lower
  // the MAX only to the best attempt, never below it — a weaker later set
  // is fatigue within the session, not proof of a lower ceiling.
  suggestedKg: number;
  e1RM: number; // the representative (best qualifying) e1RM, for transparency
  currentMaxKg: number;
  weeksSinceMax: number; // floored whole weeks since the MAX was set
  fromSet: { weightKg: number; reps: number };
};

// Is this set a true ceiling attempt eligible to LOWER a MAX? Weighted
// only — epley() needs a real lifted load and the MAX surface is keyed on
// absolute kg (bodyweight progressions surface via isSetPR, never here).
// Maximal effort = RIR 0 OR fail; reps within the Epley cap (12).
//
// Note: a `fail` set is exactly the maximal-effort signal we want for the
// LOWER path — its `reps` are the clean reps completed (AP-0p model: the
// failed rep was already decremented at log time) and `fail` is a pure
// label, so epley(weight, reps) is valid ceiling evidence, the same rule
// computeE1rmKg uses for the RAISE path.
const isCeilingAttempt = (
  s: WorkoutSet,
): s is Extract<WorkoutSet, { type: "weighted" }> => {
  if (s.type !== "weighted") return false;
  // Maximal effort = RIR 0 OR fail, via the shared fail⇒RIR-0 predicate
  // (one definition, shared with the AP-2 hard-set accumulator).
  const maximal = effectiveRir(s) === 0;
  if (!maximal) return false;
  if (!isE1rmRepCount(s.reps)) return false;
  if (s.weight <= 0) return false;
  return true;
};

// Per-exercise regression check over one session's sets. Returns the
// proposed lower MAX, or null when no downward revision is warranted.
// All four gates must hold (see file header). `nowMs` is injected for
// testability.
export const detectMaxRegression = (params: {
  sets: readonly WorkoutSet[];
  currentMax: CurrentMax | null | undefined;
  // Best qualifying e1RM (kg) over the recent corroboration window
  // (MAX_REGRESSION_RECENT_WEEKS), EXCLUDING the just-saved session.
  // Effort-agnostic on this side: a high e1RM proves capacity regardless
  // of how the set felt (mirrors the RAISE path — any set is evidence).
  // When a recent session was at least as strong as today's best ceiling
  // attempt, today is a light/off day, not a sustained decline → no lower.
  // Optional: omitted ⇒ legacy single-session behaviour (no corroboration).
  recentBestE1RM?: number;
  nowMs: number;
}): MaxRegression | null => {
  const cur = params.currentMax;
  if (cur == null) return null; // nothing to lower (manual seed required first)

  // Staleness gate: a full cycle must have passed since the MAX was set.
  const ageMs = params.nowMs - new Date(cur.setAt).getTime();
  if (ageMs < MAX_REGRESSION_STALE_WEEKS * WEEK_MS) return null;
  const weeksSinceMax = Math.floor(ageMs / WEEK_MS);

  // Best (highest e1RM) qualifying ceiling attempt in the session.
  let best: { e1RM: number; weightKg: number; reps: number } | null = null;
  for (const s of params.sets) {
    if (!isCeilingAttempt(s)) continue;
    const e = epley(s.weight, s.reps);
    if (!best || e > best.e1RM) {
      best = { e1RM: e, weightKg: s.weight, reps: s.reps };
    }
  }
  if (!best) return null;

  // Recent-ceiling gate (Antenore 2026-06-26): refuse to lower when a
  // session inside the corroboration window already proved a qualifying
  // e1RM at or above today's strongest ceiling attempt. That earlier
  // session shows the ceiling has NOT actually dropped — today's lighter
  // grind is an off/light day, not a sustained decline. (Without this,
  // a single light-day RIR0 back-off set ratchets the MAX down past a
  // recent stronger session — the incline-press false positive.)
  if (
    params.recentBestE1RM != null &&
    params.recentBestE1RM >= best.e1RM
  ) {
    return null;
  }

  // Below the noise band? Even the strongest ceiling attempt must fall
  // ≥ δ below the stored MAX to count as a real decline.
  if (best.e1RM >= cur.valueKg * (1 - MAX_REGRESSION_DELTA)) return null;

  const rounded = roundToHalfKg(best.e1RM);
  // Guard: rounding mustn't land on/above the current MAX (would be a
  // no-op or an accidental raise).
  if (rounded >= cur.valueKg) return null;

  return {
    suggestedKg: rounded,
    e1RM: best.e1RM,
    currentMaxKg: cur.valueKg,
    weeksSinceMax,
    fromSet: { weightKg: best.weightKg, reps: best.reps },
  };
};

// Whole-workout aggregation: one regression per exercise (if any). Mirror
// of suggestMaxUpdatesForWorkout — used by the post-save UX so the
// athlete sees one prompt per exercise, not one per set.
export const suggestMaxRegressionsForWorkout = (params: {
  workout: Workout;
  currentMaxes: Record<string, CurrentMax | null | undefined>;
  // Per-exercise best qualifying e1RM over the recent corroboration
  // window, EXCLUDING the just-saved workout (see detectMaxRegression).
  // Built from the recent-history cache by the caller; absent slugs =
  // no recent evidence ⇒ legacy single-session behaviour for that lift.
  recentBestE1RM?: Record<string, number>;
  nowMs: number;
}): Record<string, MaxRegression> => {
  const out: Record<string, MaxRegression> = {};
  for (const entry of params.workout.entries) {
    const exId = entry.exerciseId;
    const reg = detectMaxRegression({
      sets: entry.sets,
      currentMax: params.currentMaxes[exId],
      recentBestE1RM: params.recentBestE1RM?.[exId],
      nowMs: params.nowMs,
    });
    if (reg) out[exId] = reg;
  }
  return out;
};

// Best qualifying e1RM (kg) per exercise over a trailing window, derived
// from a list of already-fetched recent workouts. Mirrors the RAISE-side
// evidence rule (computeE1rmKg): any weighted set within the Epley rep cap
// is valid 1RM evidence, INCLUDING a fail (AP-0p model: its `reps` are the
// clean reps done, `fail` is a pure label). Pure — the caller supplies the
// recent-history window and `nowMs`. Used to feed `recentBestE1RM` into the
// regression gate so one light day (or a strong fail) can't wrongly pull a
// MAX below a recent stronger session.
export const bestE1rmByExerciseInWindow = (params: {
  workouts: readonly Workout[];
  nowMs: number;
  windowWeeks?: number;
}): Record<string, number> => {
  const cutoffMs =
    params.nowMs - (params.windowWeeks ?? MAX_REGRESSION_RECENT_WEEKS) * WEEK_MS;
  const out: Record<string, number> = {};
  for (const w of params.workouts) {
    const startedMs = new Date(w.startedAt).getTime();
    if (!Number.isFinite(startedMs) || startedMs < cutoffMs) continue;
    for (const entry of w.entries) {
      for (const s of entry.sets) {
        if (s.type !== "weighted") continue;
        if (!isE1rmRepCount(s.reps)) continue;
        if (s.weight <= 0) continue;
        const e = epley(s.weight, s.reps);
        const prev = out[entry.exerciseId] ?? 0;
        if (e > prev) out[entry.exerciseId] = e;
      }
    }
  }
  return out;
};
